import React, { useState, useEffect } from 'react';
import { api } from '../contexts/AuthContext';
import { toast } from 'sonner';
import LatestPatchNotesCard from '../components/LatestPatchNotesCard';
import DailyAveragesCard from '../components/DailyAveragesCard';
import HourlyBreakdownCard from '../components/HourlyBreakdownCard';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import {
  Ticket,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Users,
  Hotel,
  TrendingUp,
  Award,
  Loader2,
  RefreshCw,
  Smartphone,
  QrCode,
  Wrench,
  ShieldAlert
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Switch } from '../components/ui/switch';

const statusColors = {
  aperto: '#3B82F6',
  assegnato: '#8B5CF6',
  in_lavorazione: '#06B6D4',
  in_attesa_pezzi: '#A855F7',
  completato: '#22C55E',
  chiuso: '#64748B'
};

const statusLabels = {
  aperto: 'Aperto',
  assegnato: 'Assegnato',
  in_lavorazione: 'In Lavorazione',
  in_attesa_pezzi: 'In Attesa Pezzi',
  completato: 'Completato',
  chiuso: 'Chiuso'
};

const SuperAdminDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [togglingMaintenance, setTogglingMaintenance] = useState(false);
  const [error, setError] = useState(null);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const { data: dashboardData } = await api.get('/dashboard/global');
      setData(dashboardData);
      setError(null);
    } catch (err) {
      setError('Errore nel caricamento dei dati');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    // Check maintenance status
    api.get('/maintenance-status').then(res => {
      setMaintenanceMode(res.data.maintenance || false);
    }).catch(() => {});
  }, []);

  const toggleMaintenance = async () => {
    try {
      setTogglingMaintenance(true);
      const newState = !maintenanceMode;
      await api.post('/maintenance-mode', {
        enabled: newState,
        message: newState ? 'App in manutenzione per aggiornamento. Riprova tra poco.' : ''
      });
      setMaintenanceMode(newState);
      toast.success(newState ? 'Modalita manutenzione ATTIVATA — solo admin possono accedere' : 'Modalita manutenzione DISATTIVATA — tutti possono accedere');
    } catch (err) {
      toast.error('Errore nel cambio modalita');
    } finally {
      setTogglingMaintenance(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12" data-testid="dashboard-error">
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={fetchDashboard} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Riprova
        </Button>
      </div>
    );
  }

  const statusData = Object.entries(data?.status_breakdown || {}).map(([key, value]) => ({
    name: statusLabels[key] || key,
    value,
    color: statusColors[key] || '#64748B'
  }));

  const totalStatus = statusData.reduce((s, x) => s + (x.value || 0), 0) || 1;

  // Palette varia per gli hotel (deterministica per ordine)
  const HOTEL_PALETTE = ['#3B82F6', '#A855F7', '#F59E0B', '#10B981', '#EC4899', '#06B6D4', '#EF4444', '#8B5CF6', '#F97316', '#14B8A6'];
  const hotelData = (data?.tickets_by_hotel || []).map((h, i) => ({
    name: h.hotel_name || 'Hotel',
    tickets: h.count,
    fill: HOTEL_PALETTE[i % HOTEL_PALETTE.length],
  }));

  return (
    <div className="space-y-6" data-testid="super-admin-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Dashboard Globale</h1>
          <p className="text-slate-400 mt-0.5 text-sm">Panoramica del gruppo Invest Hospitality</p>
        </div>
        <Button onClick={fetchDashboard} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Aggiorna
        </Button>
      </div>

      {/* Medie giornaliere ticket + fasce orarie */}
      <DailyAveragesCard showHotels={true} extraCard={<HourlyBreakdownCard />} />

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="stat-card-info animate-fade-in stagger-1" data-testid="stat-total-tickets">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-400">Ticket Totali</p>
                <p className="text-3xl font-black text-slate-900 mt-1.5">{data?.total_tickets || 0}</p>
              </div>
              <div className="w-11 h-11 bg-[#1e3a5f]/10 rounded-xl flex items-center justify-center">
                <Ticket className="w-5 h-5 text-[#1e3a5f]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card-warning animate-fade-in stagger-2" data-testid="stat-open-tickets">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-400">Ticket Aperti</p>
                <p className="text-3xl font-black text-amber-600 mt-1.5">{data?.open_tickets || 0}</p>
              </div>
              <div className="w-11 h-11 bg-amber-100 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card-critical animate-fade-in stagger-3" data-testid="stat-critical-tickets">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-400">Ticket Critici</p>
                <p className="text-3xl font-black text-red-600 mt-1.5">{data?.critical_tickets || 0}</p>
              </div>
              <div className="w-11 h-11 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card-success animate-fade-in stagger-4" data-testid="stat-completed-today">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-400">Completati Oggi</p>
                <p className="text-3xl font-black text-emerald-600 mt-1.5">{data?.completed_today || 0}</p>
              </div>
              <div className="w-11 h-11 bg-emerald-100 rounded-xl flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status breakdown — Horizontal progress bars */}
        <Card className="border-slate-200/80 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Ticket per Stato</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3.5">
              {statusData.map((item) => {
                const pct = totalStatus > 0 ? (item.value / totalStatus) * 100 : 0;
                return (
                  <div key={item.name}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color, boxShadow: `0 0 6px ${item.color}55` }} />
                        <span className="text-sm font-medium text-slate-700">{item.name}</span>
                      </div>
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-base font-bold tabular-nums text-slate-900">{item.value}</span>
                        <span className="text-xs text-slate-500 tabular-nums">{pct.toFixed(0)}%</span>
                      </div>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${pct}%`,
                          background: `linear-gradient(90deg, ${item.color} 0%, ${item.color}cc 100%)`,
                        }}
                      />
                    </div>
                  </div>
                );
              })}
              {statusData.length === 0 && (
                <div className="text-center py-8 text-sm text-slate-500">Nessun ticket nel periodo</div>
              )}
            </div>
            <div className="mt-5 pt-4 border-t border-slate-200/60 flex items-center justify-between text-xs">
              <span className="text-slate-500">Totale ticket</span>
              <span className="font-bold tabular-nums text-slate-900">{totalStatus}</span>
            </div>
          </CardContent>
        </Card>

        {/* Tickets by hotel — un colore per hotel */}
        <Card className="border-slate-200/80 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Ticket per Hotel</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={hotelData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }} />
                  <Bar dataKey="tickets" radius={[0, 6, 6, 0]}>
                    {hotelData.map((entry, index) => (
                      <Cell key={`cell-hotel-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            {/* Legend con bullet colorati per hotel */}
            <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-slate-200/60">
              {hotelData.map((h) => (
                <div key={h.name} className="flex items-center gap-1.5 text-xs">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: h.fill }} />
                  <span className="text-slate-600 font-medium">{h.name}</span>
                  <span className="text-slate-400 tabular-nums">· {h.tickets}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Technician ranking */}
      <Card className="border-slate-200/80 shadow-sm" data-testid="technician-ranking">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Ranking Tecnici</CardTitle>
          <Award className="w-5 h-5 text-amber-500" />
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-64">
            <div className="space-y-3">
              {(data?.technician_ranking || []).map((tech, index) => (
                <div
                  key={tech._id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                      index === 0 ? 'bg-amber-100 text-amber-700' :
                      index === 1 ? 'bg-slate-200 text-slate-700' :
                      index === 2 ? 'bg-orange-100 text-orange-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {index + 1}
                    </div>
                    <span className="font-medium text-slate-900">{tech.name}</span>
                  </div>
                  <Badge variant="secondary" className="bg-green-100 text-green-800">
                    {tech.completed} completati
                  </Badge>
                </div>
              ))}
              {(!data?.technician_ranking || data.technician_ranking.length === 0) && (
                <p className="text-center text-slate-500 py-8">Nessun dato disponibile</p>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Maintenance Mode + QR Code */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Maintenance Mode Toggle */}
        <Card className={`border-slate-200 ${maintenanceMode ? 'bg-amber-50 border-amber-300' : ''}`} data-testid="maintenance-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${maintenanceMode ? 'bg-amber-200' : 'bg-slate-100'}`}>
                  <ShieldAlert className={`w-5 h-5 ${maintenanceMode ? 'text-amber-700' : 'text-slate-500'}`} />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900">Modalita Manutenzione</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {maintenanceMode ? 'ATTIVA — solo admin possono accedere' : 'Disattivata — accesso normale'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {togglingMaintenance && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                <Switch
                  checked={maintenanceMode}
                  onCheckedChange={toggleMaintenance}
                  disabled={togglingMaintenance}
                  data-testid="maintenance-toggle"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* QR Code - Install App */}
        <Card className="border-slate-200 bg-gradient-to-br from-[#1e3a5f] to-[#0f172a]" data-testid="qr-install-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-6">
            <div className="bg-white p-3 rounded-xl shadow-lg flex-shrink-0">
              <QRCodeSVG
                value={window.location.origin}
                size={120}
                level="M"
                fgColor="#1e3a5f"
                includeMargin={false}
              />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Smartphone className="w-5 h-5 text-[#d4af37]" />
                <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Installa HostFix</h3>
              </div>
              <p className="text-slate-300 text-sm leading-relaxed mb-3">
                Scansiona il QR code con il telefono per accedere all'app. Una volta aperta, clicca su "Aggiungi alla schermata Home" per installarla come app nativa.
              </p>
              <div className="flex flex-wrap gap-4 text-xs text-slate-400">
                <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> Android (Chrome)</span>
                <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> iPhone (Safari)</span>
                <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> Notifiche push</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      </div>

      {/* Ultime novità */}
      <LatestPatchNotesCard limit={3} />

      {/* Developer Credits */}
      <div className="pt-8 pb-4" data-testid="developer-credits">
        <div className="flex items-center justify-center gap-3 text-slate-300">
          <div className="h-px flex-1 bg-slate-200/60" />
          <p className="text-[11px] font-medium tracking-wide uppercase whitespace-nowrap">
            Developed by <span className="text-slate-400 font-bold">PTK</span>
          </p>
          <div className="h-px flex-1 bg-slate-200/60" />
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
